import {
  createAnalyserTap,
  type AnalyserTap,
} from '@erp/ui/hooks/use-audio-analyser';
import {
  createFrameRelay,
  type FrameRelay,
} from '@erp/ui/lib/audio/frame-source';
import type { VisualFrame } from '@erp/ui/lib/audio/types';
import { rpcClient } from '@/api/rpc/client';
import config from 'src/config';
import { MicCapture } from './capture';
import { PlaybackQueue } from './playback';
import { buildVoiceCallUrl, dispatchLiveEvent } from './liveEvents';
import {
  collectRequest,
  TranscriptBuffer,
  toCommentary,
} from './transcriptBuffer';
import {
  CallTurns,
  isBackchannel,
  type CallRole,
  type LiveSpeech,
} from './callTurns';
import { SpeechSync } from './speechSync';
import { IdleTimer } from './idleTimer';
import { CrossTalk } from './crossTalk';

export type VoiceCallStatus = 'idle' | 'connecting' | 'live';
export type VoiceCallFailure =
  'start-failed' | 'microphone' | 'disconnected' | 'idle';

export interface VoiceCallHandlers {
  /**
   * Run one request through the chat agent and resolve with its reply text.
   * Called for each GPT-Live client delegation, with the caller's words.
   */
  onDelegate: (request: string) => Promise<string>;
  /**
   * A spoken exchange the assistant handled by voice alone ("hello" → "hi"),
   * one finished turn at a time, in order — to be saved to the thread.
   */
  onVoiceTurn: (role: CallRole, text: string) => void;
  /**
   * Called when a call fails to start, loses the mic, drops, or is hung up
   * because nobody talked (see {@link IDLE_WARNING_MS}).
   */
  onError?: (reason: VoiceCallFailure) => void;
}

/** What a render shows of the call. Replaced, never mutated, on change. */
export interface VoiceCallState {
  status: VoiceCallStatus;
  /** What is still being said, streamed as it is spoken; not yet messages. */
  live: LiveSpeech;
  /** The caller's mic is muted: the call stays up, the voice can't hear. */
  muted: boolean;
}

const REQUEST_TIMING = { quietMs: 500, maxWaitMs: 2_000, pollMs: 50 };
const ANALYSER_OPTIONS = { bands: 16, smoothing: 0.6 } as const;
/**
 * A live call with no one talking for this long gets a spoken warning that
 * it is about to end, and hangs up {@link IDLE_GRACE_MS} later if the caller
 * still says nothing: GPT-Live bills by the minute, silence included. The
 * countdown pauses while a delegated request runs, so a caller waiting on an
 * answer is never hung up on.
 */
export const IDLE_WARNING_MS = 20_000;
/** From the warning to hanging up; the warning itself takes ~5 s to say. */
export const IDLE_GRACE_MS = 15_000;

function sendEvent(
  socket: WebSocket | null,
  event: Record<string, unknown>,
): void {
  if (socket?.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify(event));
  }
}

/** Ask GPT-Live to end the session, then drop the socket. */
function hangUpSocket(socket: WebSocket): void {
  sendEvent(socket, { type: 'session.close' });
  if (socket.readyState <= WebSocket.OPEN) socket.close();
}

/** What GPT-Live should say for one delegated request. */
export async function replyFor(
  request: string,
  onDelegate: (request: string) => Promise<string>,
): Promise<string> {
  if (!request)
    return "I didn't catch that request. Ask the user to repeat it.";
  try {
    return await onDelegate(request);
  } catch {
    return 'That request failed. Tell the user and offer to try again.';
  }
}

/**
 * A full-duplex GPT-Live voice call in front of the chat agent: the socket,
 * the mic, playback and the transcript, outside React. React reads
 * {@link VoiceCallState} through `subscribe` / `getSnapshot`.
 *
 * GPT-Live holds the conversation; whenever it decides it needs data or an
 * action, it opens a client delegation. This turns that into an ordinary
 * chat-agent turn (`onDelegate`) and speaks the reply back through
 * `session.commentary.append`, so every chat-agent tool works on a call.
 *
 * Methods are arrow properties so they can be handed out as callbacks.
 */
export class VoiceCallSession {
  /** Visual frames of the caller's mic and of the assistant's voice. Stable:
   * the visualizers subscribe once and survive reconnects. */
  readonly micVisual: FrameRelay<VisualFrame> = createFrameRelay();
  readonly assistantVisual: FrameRelay<VisualFrame> = createFrameRelay();

  private state: VoiceCallState = {
    status: 'idle',
    live: { answered: '', assistant: '', user: '', filler: '' },
    muted: false,
  };
  private readonly listeners = new Set<() => void>();
  private handlers: VoiceCallHandlers = {
    onDelegate: () => Promise.reject(new Error('No delegate yet')),
    onVoiceTurn: () => undefined,
  };

  private socket: WebSocket | null = null;
  /** Bumped by every teardown, so a start that outlives its call stops. */
  private callId = 0;
  private mic: MicCapture | null = null;
  private playback: PlaybackQueue | null = null;
  private taps: AnalyserTap[] = [];
  private transcript = new TranscriptBuffer();
  /** Counts down to hanging up a quiet call; one per live call. */
  private idle: IdleTimer | null = null;
  private readonly timers = new Set<ReturnType<typeof setTimeout>>();
  /** Session-timeline position of the end of the output audio so far. */
  private outputMs = 0;
  private readonly callTurns = new CallTurns((role, text) =>
    this.handlers.onVoiceTurn(role, text),
  );
  /** A timer that hang-up cancels. */
  private readonly schedule = (callback: () => void, delayMs: number): void => {
    const timer = setTimeout(() => {
      this.timers.delete(timer);
      callback();
    }, delayMs);
    this.timers.add(timer);
  };
  private readonly audioNow = (): number => this.playback?.currentTime ?? 0;
  private readonly crossTalk = new CrossTalk({
    audioNow: this.audioNow,
    schedule: this.schedule,
  });
  private readonly speechSync = new SpeechSync({
    audioNow: this.audioNow,
    now: () => Date.now(),
    schedule: this.schedule,
    release: (delta) => {
      // The assistant is still talking: not idle.
      this.idle?.touch();
      this.callTurns.assistantSpoke(delta);
      this.refreshLive();
    },
  });

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): VoiceCallState => this.state;

  /** The latest callbacks from the component; read when they are called. */
  setHandlers = (handlers: VoiceCallHandlers): void => {
    this.handlers = handlers;
  };

  start = async (): Promise<void> => {
    // A playback queue exists from the click until hang-up.
    if (this.playback) return;
    const callId = this.callId;
    this.update({ status: 'connecting' });
    // Inside the click: an AudioContext made here is allowed to play.
    this.startPlayback();
    try {
      const { ticket, wsPath } = await rpcClient.ai.voiceCall.mintTicket();
      // Hung up (or unmounted) while the ticket was minted: stay hung up.
      if (this.callId !== callId) return;
      const socket = new WebSocket(
        buildVoiceCallUrl(
          config.apiBaseUrl,
          wsPath,
          ticket,
          window.location.origin,
        ),
      );
      this.socket = socket;
      socket.onmessage = (message: MessageEvent<string>) =>
        this.handleMessage(message.data);
      socket.onclose = () => {
        if (this.socket === socket) this.hangUp();
      };
      socket.onerror = () => {
        if (this.socket !== socket) return;
        this.handlers.onError?.('disconnected');
        this.hangUp();
      };
      socket.onopen = () => void this.startMic();
    } catch {
      if (this.callId !== callId) return;
      this.handlers.onError?.('start-failed');
      this.hangUp();
    }
  };

  /** Ends the call, keeping what was said. Safe to call when idle. */
  hangUp = (): void => {
    // Any start still awaiting the ticket or the socket is now stale.
    this.callId++;
    this.idle?.stop();
    this.idle = null;
    this.micVisual.setSource(null);
    this.assistantVisual.setSource(null);
    this.taps.forEach((tap) => tap.dispose());
    this.taps = [];
    this.mic?.stop();
    this.mic = null;
    this.playback?.close();
    this.playback = null;
    // Settle the conversation so far: the last turns are kept.
    const buffer = this.transcript;
    this.callTurns.assistantSpoke(this.speechSync.drain());
    this.callTurns.end(buffer.commitAnswered());
    const unanswered = buffer.text();
    if (unanswered) this.handlers.onVoiceTurn('user', unanswered);
    // A new buffer per call: a delegation still collecting from the old one
    // can't take the next call's words.
    this.transcript = new TranscriptBuffer();
    // Words for audio that will now never play are never shown.
    this.speechSync.reset();
    this.crossTalk.reset();
    this.outputMs = 0;
    this.timers.forEach(clearTimeout);
    this.timers.clear();
    const socket = this.socket;
    this.socket = null;
    if (socket) hangUpSocket(socket);
    this.update({ status: 'idle', muted: false, live: this.liveSpeech() });
  };

  toggleMute = (): void => {
    this.idle?.heard();
    const muted = !this.state.muted;
    this.update({ muted });
    this.send({
      type: muted ? 'session.input_audio.mute' : 'session.input_audio.unmute',
    });
  };

  /**
   * Tell the voice that the user shared their screen or a file with the
   * assistant, so it delegates questions about it instead of saying it can't
   * see it.
   */
  noteShared = (kind: 'screen' | 'file'): void => {
    this.idle?.heard();
    this.send({ type: 'chat_agent.context_shared', kind });
  };

  private update(change: Partial<VoiceCallState>): void {
    this.state = { ...this.state, ...change };
    this.listeners.forEach((listener) => listener());
  }

  private liveSpeech(): LiveSpeech {
    return {
      answered: this.transcript.answeredText(),
      assistant: this.callTurns.liveAssistant(),
      user: this.transcript.pendingText(),
      filler: this.callTurns.liveFiller(),
    };
  }

  // Many transcript deltas a second: notify only when something changed.
  private refreshLive(): void {
    const prev = this.state.live;
    const next = this.liveSpeech();
    if (
      prev.answered !== next.answered ||
      prev.assistant !== next.assistant ||
      prev.user !== next.user ||
      prev.filler !== next.filler
    ) {
      this.update({ live: next });
    }
  }

  private send(event: Record<string, unknown>): void {
    sendEvent(this.socket, event);
  }

  private async handleDelegation(delegationId: string): Promise<void> {
    const callId = this.callId;
    // The caller is waiting on this: never idle while it runs.
    const release = this.idle?.hold();
    try {
      const request = await collectRequest(this.transcript, REQUEST_TIMING);
      // Hung up meanwhile: the request belonged to a call that is over.
      if (this.callId !== callId) return;
      this.refreshLive();
      const reply = await replyFor(request, (text) =>
        this.handlers.onDelegate(text),
      );
      if (this.callId !== callId) return;
      this.send({
        type: 'session.commentary.append',
        delegation_id: delegationId,
        content: toCommentary(reply),
      });
      this.callTurns.replyDelivered();
    } finally {
      release?.();
    }
  }

  private warnIdle = (): void => {
    // The warning is its own bubble, not tacked onto the greeting before it.
    this.callTurns.assistantSpoke(this.speechSync.drain());
    this.callTurns.settle(this.transcript.commitAnswered());
    this.refreshLive();
    this.send({
      type: 'chat_agent.idle_warning',
      seconds: IDLE_GRACE_MS / 1000,
    });
  };

  private hangUpIdle = (): void => {
    this.handlers.onError?.('idle');
    this.hangUp();
  };

  /** The caller started a new turn. */
  private callerMovedOn(startMs: number): void {
    // Said over the assistant: its turn is not cut into pieces, one per
    // word. Settled once, when it has gone quiet or starts replying.
    if (this.crossTalk.talking) {
      this.crossTalk.whenQuiet(startMs, this.settleCrossTalk);
    } else {
      this.settleExchange();
    }
  }

  /**
   * The caller moved on: settle the exchange before, last words included, so
   * it is saved whole and in order.
   */
  private settleExchange(): void {
    this.callTurns.assistantSpoke(this.speechSync.drain());
    this.callTurns.callerSpoke(this.transcript);
  }

  /** The assistant went quiet after the caller talked over it. */
  /**
   * The caller talked over the assistant, which has now gone quiet or begun
   * its reply at `replyAt` on the session timeline.
   */
  private settleCrossTalk = (replyAt: number | null): void => {
    const said = this.transcript.pendingText();
    // Taken by a delegation meanwhile: that settled it.
    if (!said) return;
    // "Mhm" while it talked: listening, not a turn. Unless it asked: then
    // "yes" is the answer.
    if (isBackchannel(said) && !this.callTurns.askedQuestion()) {
      this.transcript.dropPending();
    } else {
      // Words it never got to say were never said; from the reply on, text
      // waits for its audio as the next turn's.
      this.speechSync.dropUnplayed(replyAt ?? Number.POSITIVE_INFINITY);
      this.callTurns.assistantSpoke(this.speechSync.drainScheduled());
      this.callTurns.cutOff();
      this.callTurns.callerSpoke(this.transcript);
      // The caller's words since are what the reply answers.
      if (replyAt !== null) this.transcript.noteAssistantSpeech(replyAt);
    }
    this.refreshLive();
  };

  private handleMessage(raw: string): void {
    dispatchLiveEvent(raw, {
      started: () => {
        this.idle = new IdleTimer({
          warnAfterMs: IDLE_WARNING_MS,
          endAfterMs: IDLE_GRACE_MS,
          onWarn: this.warnIdle,
          onIdle: this.hangUpIdle,
        });
        this.idle.start();
        this.update({ status: 'live' });
        this.playback?.chime();
      },
      audio: (base64) => {
        const played = this.playback?.enqueue(base64);
        if (!played) return;
        // The assistant is talking, transcribed or not. The stream carries
        // silence too, so only an audible frame counts.
        if (played.audible) {
          this.idle?.touch();
          this.crossTalk.noteAudible(played.playsAt + played.seconds);
        }
        // Output audio frames carry no timestamps, but the stream is one
        // continuous track from the session's start (silence included), so
        // its position on the session timeline is the audio received so far.
        const startMs = this.outputMs;
        this.outputMs = startMs + played.seconds * 1000;
        this.speechSync.noteAudio(startMs, this.outputMs, played.playsAt);
      },
      userSpeech: (fragment) => {
        this.idle?.heard();
        // A late fragment of the turn being answered is not a new turn.
        const late = this.transcript.isLate(fragment);
        this.transcript.push(fragment);
        if (!late) this.callerMovedOn(fragment.start_ms);
        this.refreshLive();
      },
      assistantSpeech: (delta, startMs, endMs) => {
        // The reply to a caller who cut in: the interrupted answer ends here.
        if (
          this.crossTalk.noteText(startMs, endMs) &&
          this.crossTalk.cancel()
        ) {
          this.settleCrossTalk(startMs);
        }
        // Only the boundary moving changes what is shown.
        if (this.transcript.noteAssistantSpeech(startMs)) this.refreshLive();
        this.speechSync.addText(delta, startMs);
      },
      delegation: (id) => {
        // Said over the voice, then delegated before it went quiet: the
        // exchange before is settled now, so the request is only the new
        // words and the earlier answer is not taken for filler. Only words
        // already playing count: later text is the filler for this request,
        // and a "mhm" plus filler is not an answer that ends a turn.
        if (this.crossTalk.cancel()) {
          this.callTurns.assistantSpoke(this.speechSync.drainScheduled());
          this.callTurns.callerSpoke(this.transcript);
        }
        // The words before the filler are the request after all.
        this.transcript.cancelAnswered();
        // Words already on their way out are part of the filler.
        this.callTurns.assistantSpoke(this.speechSync.drain());
        this.callTurns.delegationStarted();
        this.refreshLive();
        void this.handleDelegation(id);
      },
      closed: this.hangUp,
    });
  }

  private async startMic(): Promise<void> {
    const mic = new MicCapture({
      onFrame: (audio) => {
        // Muted: nothing leaves the browser at all.
        if (!this.state.muted) {
          this.send({ type: 'session.input_audio.append', audio });
        }
      },
      // Sound before it is transcribed: a caller who starts talking just
      // before the warning is not interrupted by it. Only a touch: the
      // warning leaking from the speakers must not cancel itself, so after
      // it only the transcript counts.
      onVoice: () => {
        if (!this.state.muted) this.idle?.touch();
      },
    });
    this.mic = mic;
    try {
      await mic.start();
    } catch {
      if (this.mic !== mic) return;
      this.handlers.onError?.('microphone');
      this.hangUp();
      return;
    }
    // Hung up while asking for the mic: the capture already let it go.
    if (this.mic !== mic) return;
    const input = mic.input;
    if (!input) return;
    const tap = createAnalyserTap(input.context, input.node, ANALYSER_OPTIONS);
    this.taps.push(tap);
    this.micVisual.setSource(tap.visual);
  }

  private startPlayback(): void {
    const playback = new PlaybackQueue();
    this.playback = playback;
    const output = playback.output();
    const tap = createAnalyserTap(
      output.context,
      output.node,
      ANALYSER_OPTIONS,
    );
    this.taps.push(tap);
    this.assistantVisual.setSource(tap.visual);
  }
}
